<script lang="ts" setup>
  import {
    ContentBlockAudioData,
    ContentBlockTypeId,
  } from '~/model/ContentBlockData/ContentBlockData'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import { IconBoxedFacet } from '~/components/IconBoxed/IconBoxedFacet'
  import { VisualThemeId } from '~/model/VisualTheme/VisualTheme'

  interface Props extends UseBemProps, ContentBlockAudioData {
    type: ContentBlockTypeId
    facets?: Array<string>
    audio: string
    legend?: string
    author: string
    origin?: string
  }

  const props = defineProps<Props>()
  const { bemAdd, bemFacets } = useBem('c-content-block-audio', props, {})

  const rootClasses = computed<Array<string>>(() => {
    return [...bemFacets.value, bemAdd(props.legend ? 'has-legend' : '')]
  })

  const figcaption = computed<string>(() => {
    if (props.author || props.origin) {
      return `${props.author}${props.origin ? ', ' : ''}`
    }

    return ''
  })
</script>

<template>
  <div :class="rootClasses" class="c-content-block-audio">
    <div class="c-content-block-audio__box">
      <div class="c-content-block-audio__icon">
        <IconBoxed
          :facets="[IconBoxedFacet.XXXLarge]"
          :visual-theme="VisualThemeId.Negative"
          class="c-content-block-audio__symbol"
        >
          <template #letter>
            {{ 'A' }}
          </template>
        </IconBoxed>
      </div>
      <div class="c-content-block-audio__audio">
        <h2 v-if="legend" class="c-content-block-audio__legend u-reset u-reset--h3">
          {{ legend }}
        </h2>
        <ClientOnly>
          <WaveSurfer class="c-content-block-audio__wave-surfer" :src="audio" />
        </ClientOnly>
      </div>
    </div>
    <figcaption v-if="figcaption" class="c-content-block-audio__source">
      — {{ figcaption }}<cite v-if="origin">{{ origin }}</cite>
    </figcaption>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;
  @use '/assets/scss/base/typography/typography' as type;

  $offset: 4px;

  .c-content-block-audio {
    overflow: hidden;
    grid-column: 4 / 10;
  }

  .c-content-block-audio__box {
    @include anaglyph.with-shadow-apply(mono, $offset, $offset, 0, 0, col.$monochrome-lead);
    background-color: col.$variant-cyan-light;
    padding: 16px;
    margin: 0 $offset $offset 0;

    display: flex;
    flex-flow: row nowrap;
    column-gap: 24px;
  }

  .c-content-block-audio__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 88px;
    height: 88px;
  }

  .c-content-block-audio__audio {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    flex: 0 1 100%;
    row-gap: 12px;
  }

  .c-content-block-audio__wave-surfer {
    //height: 40px;
  }

  .c-content-block-audio__source {
    @include type.h4;
    padding: 12px 40px;
    text-align: right;
  }

  .c-content-block-audio--has-legend {
    .c-content-block-audio__audio {
      justify-content: space-between;
    }
  }
</style>
